import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react"

import type { ReportLayoutWidgetDto, ReportTemplateFlowSectionDto, ReportTemplateLayoutDto } from "../../api/reportTemplateService"
import {
  DEFAULT_MANUAL_PAGE_COUNT,
  MAX_MANUAL_PAGE_COUNT,
  MIN_FLOW_SECTION_HEIGHT,
  MIN_WIDGET_HEIGHT,
  MIN_WIDGET_WIDTH,
  clamp,
  createWidgetId,
  getLayoutFlowSections,
  getWidgetFlowSectionId,
  getWidgetManualPage,
  nextWidgetZIndex,
} from "./editorUtils"
import type { LayoutSectionKey } from "./layoutTypes"

type UseLayoutSectionManagementParams = {
  layout: ReportTemplateLayoutDto
  setLayout: Dispatch<SetStateAction<ReportTemplateLayoutDto>>
  layoutSection: LayoutSectionKey
  setLayoutSection: Dispatch<SetStateAction<LayoutSectionKey>>
  flowSections: ReportTemplateFlowSectionDto[]
  primaryFlowSectionId: string
  selectedWidgetId: string | null
  setSelectedWidgetId: Dispatch<SetStateAction<string | null>>
}

export function useLayoutSectionManagement({
  layout,
  setLayout,
  layoutSection,
  setLayoutSection,
  flowSections,
  primaryFlowSectionId,
  selectedWidgetId,
  setSelectedWidgetId,
}: UseLayoutSectionManagementParams) {
  const [collapsedFlowSections, setCollapsedFlowSections] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setCollapsedFlowSections((prev) => {
      const sectionIds = new Set(flowSections.map((item) => item.sectionId))
      const next: Record<string, boolean> = {}
      let changed = false
      Object.entries(prev).forEach(([sectionId, value]) => {
        if (sectionIds.has(sectionId)) {
          next[sectionId] = value
        } else {
          changed = true
        }
      })
      if (!changed && Object.keys(next).length === Object.keys(prev).length) return prev
      return next
    })
  }, [flowSections])

  const setSectionWidgets = useCallback(
    (section: LayoutSectionKey, updater: (widgets: ReportLayoutWidgetDto[]) => ReportLayoutWidgetDto[]) => {
      setLayout((prev) => {
        if (section === "header") {
          const sectionState = prev.headerSection ?? {
            enabled: false,
            height: 120,
            widgets: [] as ReportLayoutWidgetDto[],
          }
          return {
            ...prev,
            headerSection: {
              ...sectionState,
              widgets: updater(sectionState.widgets ?? []),
            },
          }
        }
        if (section === "footer") {
          const sectionState = prev.footerSection ?? {
            enabled: false,
            height: 80,
            widgets: [] as ReportLayoutWidgetDto[],
          }
          return {
            ...prev,
            footerSection: {
              ...sectionState,
              widgets: updater(sectionState.widgets ?? []),
            },
          }
        }
        return {
          ...prev,
          widgets: updater(prev.widgets ?? []),
        }
      })
    },
    [setLayout],
  )

  const setHeaderSectionEnabled = useCallback(
    (enabled: boolean) => {
      setLayout((prev) => ({
        ...prev,
        headerSection: {
          ...(prev.headerSection ?? { enabled: false, height: 120, widgets: [] }),
          enabled,
        },
      }))
      if (enabled) {
        setLayoutSection("header")
      }
    },
    [setLayout, setLayoutSection],
  )

  const setFooterSectionEnabled = useCallback(
    (enabled: boolean) => {
      setLayout((prev) => ({
        ...prev,
        footerSection: {
          ...(prev.footerSection ?? { enabled: false, height: 80, widgets: [] }),
          enabled,
        },
      }))
      if (enabled) {
        setLayoutSection("footer")
      }
    },
    [setLayout, setLayoutSection],
  )

  const setHeaderSectionHeight = useCallback(
    (height: number) => {
      const safeHeight = clamp(Math.round(height || 120), 60, 360)
      setLayout((prev) => ({
        ...prev,
        headerSection: {
          ...(prev.headerSection ?? { enabled: false, height: 120, widgets: [] }),
          height: safeHeight,
        },
      }))
    },
    [setLayout],
  )

  const setFooterSectionHeight = useCallback(
    (height: number) => {
      const safeHeight = clamp(Math.round(height || 80), 60, 360)
      setLayout((prev) => ({
        ...prev,
        footerSection: {
          ...(prev.footerSection ?? { enabled: false, height: 80, widgets: [] }),
          height: safeHeight,
        },
      }))
    },
    [setLayout],
  )

  const setManualPageCount = useCallback(
    (count: number) => {
      const safeCount = clamp(Math.floor(count || 1), DEFAULT_MANUAL_PAGE_COUNT, MAX_MANUAL_PAGE_COUNT)
      setLayout((prev) => ({
        ...prev,
        manualPageCount: safeCount,
        widgets: (prev.widgets ?? []).map((widget) => {
          const currentPage = getWidgetManualPage(widget, 1)
          if (currentPage <= safeCount) return widget
          return {
            ...widget,
            props: {
              ...(widget.props && typeof widget.props === "object" ? widget.props : {}),
              manualPage: safeCount,
            },
          }
        }),
      }))
    },
    [setLayout],
  )

  const addFlowSection = useCallback(() => {
    const base = flowSections.length + 1
    const usedNames = new Set(flowSections.map((section) => section.name.toLowerCase()))
    let candidateName = `Section ${base}`
    let suffix = base
    while (usedNames.has(candidateName.toLowerCase())) {
      suffix += 1
      candidateName = `Section ${suffix}`
    }
    const sectionId = createWidgetId()
    setLayout((prev) => ({
      ...prev,
      flowSections: [
        ...getLayoutFlowSections(prev),
        {
          sectionId,
          name: candidateName,
          orderIndex: flowSections.length + 1,
          breakMode: "flow",
          minHeight: 140,
        },
      ],
    }))
  }, [flowSections, setLayout])

  const updateFlowSection = useCallback(
    (sectionId: string, patch: Partial<ReportTemplateFlowSectionDto>) => {
      setLayout((prev) => {
        const sections = getLayoutFlowSections(prev).map((section) =>
          section.sectionId === sectionId
            ? {
                ...section,
                ...patch,
                name: String((patch.name ?? section.name) || section.name).trim() || section.name,
                minHeight: clamp(Math.floor(Number(patch.minHeight ?? section.minHeight ?? 140)), MIN_FLOW_SECTION_HEIGHT, 2400),
              }
            : section,
        )
        return {
          ...prev,
          flowSections: sections.map((section, index) => ({
            ...section,
            orderIndex: index + 1,
          })),
        }
      })
    },
    [setLayout],
  )

  const moveFlowSection = useCallback(
    (sectionId: string, direction: "up" | "down") => {
      setLayout((prev) => {
        const sections = [...getLayoutFlowSections(prev)]
        const index = sections.findIndex((section) => section.sectionId === sectionId)
        if (index < 0) return prev
        const target = direction === "up" ? index - 1 : index + 1
        if (target < 0 || target >= sections.length) return prev
        const [item] = sections.splice(index, 1)
        sections.splice(target, 0, item)
        return {
          ...prev,
          flowSections: sections.map((section, sectionIndex) => ({
            ...section,
            orderIndex: sectionIndex + 1,
          })),
        }
      })
    },
    [setLayout],
  )

  const removeFlowSection = useCallback(
    (sectionId: string) => {
      if (flowSections.length <= 1) return
      const fallbackSectionId =
        flowSections.find((section) => section.sectionId !== sectionId)?.sectionId || primaryFlowSectionId
      setLayout((prev) => ({
        ...prev,
        flowSections: getLayoutFlowSections(prev)
          .filter((section) => section.sectionId !== sectionId)
          .map((section, index) => ({ ...section, orderIndex: index + 1 })),
        widgets: (prev.widgets ?? []).map((widget) => {
          const currentSection = getWidgetFlowSectionId(widget, fallbackSectionId)
          if (currentSection !== sectionId) return widget
          return {
            ...widget,
            props: {
              ...(widget.props && typeof widget.props === "object" ? widget.props : {}),
              flowSectionId: fallbackSectionId,
            },
          }
        }),
      }))
      setCollapsedFlowSections((prev) => {
        if (!(sectionId in prev)) return prev
        const next = { ...prev }
        delete next[sectionId]
        return next
      })
    },
    [flowSections, primaryFlowSectionId, setLayout],
  )

  const toggleDesignFlowSectionCollapsed = useCallback(
    (sectionId: string) => {
      setCollapsedFlowSections((prev) => {
        const nextCollapsed = !prev[sectionId]
        if (nextCollapsed && selectedWidgetId) {
          const selected = (layout.widgets ?? []).find((item) => item.id === selectedWidgetId)
          if (selected) {
            const selectedSectionId = getWidgetFlowSectionId(selected, primaryFlowSectionId)
            if (selectedSectionId === sectionId) {
              setSelectedWidgetId(null)
            }
          }
        }
        return { ...prev, [sectionId]: nextCollapsed }
      })
    },
    [layout.widgets, primaryFlowSectionId, selectedWidgetId, setSelectedWidgetId],
  )

  useEffect(() => {
    if (layoutSection === "header" && !(layout.headerSection?.enabled ?? false)) {
      setLayoutSection("body")
    }
    if (layoutSection === "footer" && !(layout.footerSection?.enabled ?? false)) {
      setLayoutSection("body")
    }
  }, [layout.footerSection?.enabled, layout.headerSection?.enabled, layoutSection, setLayoutSection])

  const moveWidgetBetweenSections = useCallback(
    (
      widgetId: string,
      sourceSection: LayoutSectionKey,
      targetSection: LayoutSectionKey,
      options: {
        x: number
        y: number
        targetCanvasWidth: number
        targetCanvasHeight: number
        flowSectionId?: string
        manualPage?: number
      },
    ) => {
      setLayout((prev) => {
        const getSectionWidgets = (section: LayoutSectionKey) => {
          if (section === "header") return prev.headerSection?.widgets ?? []
          if (section === "footer") return prev.footerSection?.widgets ?? []
          return prev.widgets ?? []
        }

        const sourceWidgets = getSectionWidgets(sourceSection)
        const targetWidgets = sourceSection === targetSection ? sourceWidgets : getSectionWidgets(targetSection)
        const widget = sourceWidgets.find((item) => item.id === widgetId)
        if (!widget) return prev

        const remainingSourceWidgets = sourceWidgets.filter((item) => item.id !== widgetId)
        const safeWidth = clamp(Math.round(Number(widget.width) || 240), MIN_WIDGET_WIDTH, options.targetCanvasWidth)
        const safeHeight =
          targetSection === "body"
            ? Math.max(MIN_WIDGET_HEIGHT, Math.round(Number(widget.height) || 56))
            : clamp(Math.round(Number(widget.height) || 56), MIN_WIDGET_HEIGHT, options.targetCanvasHeight)
        const maxX = Math.max(0, options.targetCanvasWidth - safeWidth)
        const maxY =
          targetSection === "body"
            ? Number.POSITIVE_INFINITY
            : Math.max(0, options.targetCanvasHeight - safeHeight)
        const nextProps = {
          ...(widget.props && typeof widget.props === "object" ? widget.props : {}),
          flowSectionId:
            targetSection === "body"
              ? options.flowSectionId || getWidgetFlowSectionId(widget, primaryFlowSectionId)
              : primaryFlowSectionId,
          manualPage:
            targetSection === "body"
              ? clamp(options.manualPage ?? getWidgetManualPage(widget, 1), 1, MAX_MANUAL_PAGE_COUNT)
              : 1,
        }
        const movedWidget: ReportLayoutWidgetDto = {
          ...widget,
          x: clamp(Math.round(options.x || 0), 0, maxX),
          y: clamp(Math.round(options.y || 0), 0, maxY),
          width: safeWidth,
          height: safeHeight,
          zIndex: nextWidgetZIndex(targetSection === sourceSection ? remainingSourceWidgets : targetWidgets),
          props: nextProps,
        }

        const assignSectionWidgets = (
          section: LayoutSectionKey,
          widgets: ReportLayoutWidgetDto[],
          draft: ReportTemplateLayoutDto,
        ) => {
          if (section === "header") {
            draft.headerSection = {
              ...(draft.headerSection ?? { enabled: false, height: 120, widgets: [] }),
              widgets,
            }
            return
          }
          if (section === "footer") {
            draft.footerSection = {
              ...(draft.footerSection ?? { enabled: false, height: 80, widgets: [] }),
              widgets,
            }
            return
          }
          draft.widgets = widgets
        }

        const next: ReportTemplateLayoutDto = {
          ...prev,
          widgets: prev.widgets ?? [],
          headerSection: prev.headerSection,
          footerSection: prev.footerSection,
        }

        assignSectionWidgets(sourceSection, remainingSourceWidgets, next)
        const destinationWidgets =
          sourceSection === targetSection
            ? [...remainingSourceWidgets, movedWidget]
            : [...targetWidgets, movedWidget]
        assignSectionWidgets(targetSection, destinationWidgets, next)

        if (targetSection === "header") {
          next.headerSection = {
            ...(next.headerSection ?? { enabled: false, height: 120, widgets: [] }),
            enabled: true,
          }
        }
        if (targetSection === "footer") {
          next.footerSection = {
            ...(next.footerSection ?? { enabled: false, height: 80, widgets: [] }),
            enabled: true,
          }
        }

        return next
      })

      setSelectedWidgetId(widgetId)
      setLayoutSection(targetSection)
    },
    [primaryFlowSectionId, setLayout, setLayoutSection, setSelectedWidgetId],
  )

  return {
    collapsedFlowSections,
    setSectionWidgets,
    moveWidgetBetweenSections,
    setHeaderSectionEnabled,
    setFooterSectionEnabled,
    setHeaderSectionHeight,
    setFooterSectionHeight,
    setManualPageCount,
    addFlowSection,
    updateFlowSection,
    moveFlowSection,
    removeFlowSection,
    toggleDesignFlowSectionCollapsed,
  }
}
